import { validator } from "LIB/validate-this";
import _ from "lodash";
import moment from "moment";
import * as rules from "LIB/validation/rules";
import {
  validatePurchaseForFields,
  validatePurchaseForSaleFields
} from "MODULES/PurchaseOrders/shared/validations";
import {
  AS_REMOVED,
  CORE_REPAIR_FEE_NOT_TO_EXCEED,
  HAZMAT_FEE,
  PERCENT_INSURED,
  PREFERRED_REPAIR_STATION_ID,
  RESERVE_FOR_SALE_QUANTITY,
  TOOL_USED
} from "MODULES/PurchaseOrders/constants";
import { BUYER_CREDIT_PCT, COST, QUANTITY } from "APP_ROOT/constants";
import {
  CONDITION,
  DO_NOT_SELL_REASON,
  EXCHANGE_SRP,
  INSPECTED,
  LEAD_TIME,
  NEW_ITEM,
  OVERHAULED,
  PART_ID,
  REPAIRED,
  SRP_CONDITION,
  SUGGESTED_RETAIL,
  TAG_DATE,
  TAGGED_BY,
  TRACE_TYPE,
  UNSELLABLE_PER_USER
} from "MODULES/inventories/constants";
import { DOM } from "MODULES/parts/constants";
import { PURCHASE_STATUS } from "MODULES/IncomingQuotes/constants";
import {
  CORE_DUE_DAYS,
  CORE_RETURN_DATE
} from "MODULES/RepairOrders/constants";
import { EXCHANGE_FEE } from "MODULES/OutgoingQuotes/constants";
import { INTERNAL_NOTE } from "MODULES/shared/constants";
import { useSettings } from "APP_ROOT/hooks/useCurrentUser";

export const preferredRepairStationRequiredValidationAfterDate = moment(
  "2020-01-20 07:00:00"
); // SHIM

export default function validate(values, props) {
  // The date the hazmat/exchangeSrp validations were added plus a 7 hour buffer,
  // so essentially 12/12 at midnight AZ time.
  const validationDate = moment("2018-12-13 07:00:00");
  const exchangeSrpValidationDate = moment("2019-06-14 07:00:00");
  const createdAt = moment(values.created_at || new Date());
  const skipValidationForPurchase =
    (values.purchase_for_sale &&
      Number(values.quantity) === Number(values[RESERVE_FOR_SALE_QUANTITY])) ||
    values.purchase_for_repair_assist;

  const changes = _.differenceWith(
    _.toPairs(props.initialValues),
    _.toPairs(values),
    _.isEqual
  );

  if (changes.length === 1 && _.get(changes, "[0][0]") === "quantity") {
    return validator(values, form => {
      if (values.reserve_for_sale_quantity) {
        form
          .validate(QUANTITY)
          .satisfies(
            rules.isGreaterThanOrEqualTo(values.reserve_for_sale_quantity)
          );
      } else if (values.quantity_received) {
        form
          .validate(QUANTITY)
          .satisfies(rules.isGreaterThanOrEqualTo(values.quantity_received));
      } else {
        form.validate(QUANTITY).satisfies(rules.isGreaterThan(0));
      }
    });
  }

  return validator(values, form => {
    const isConsignment =
      props.selectedLineItem && props.selectedLineItem.consignment;

    if (!isConsignment) {
      form.validate(PURCHASE_STATUS).satisfies(rules.required);
    }

    if (
      !(
        _.get(values, "part.pass_fail") ||
        _.get(props, "selectedPart.pass_fail") ||
        _.get(props, "selectedPart.tooling_part_type")
      ) &&
      createdAt.isAfter(exchangeSrpValidationDate) &&
      !values.do_not_exchange &&
      ![NEW_ITEM, AS_REMOVED].includes(values.condition) &&
      !skipValidationForPurchase
    ) {
      const minOutrightValueForExchangeSettings = useSettings()
        .min_outright_value_for_exchange_consideration;
      const suggestedRetail = parseFloat(values.suggested_retail);
      const minOutrightValue = parseFloat(minOutrightValueForExchangeSettings);
      const shouldCheckMinValue =
        minOutrightValueForExchangeSettings !== null &&
        minOutrightValueForExchangeSettings !== undefined;

      if (!shouldCheckMinValue || suggestedRetail >= minOutrightValue) {
        form
          .validate(EXCHANGE_SRP)
          .satisfies(rules.required, rules.isGreaterThan(0));
      }
    }

    form
      .validate(PART_ID, CONDITION, COST, QUANTITY, BUYER_CREDIT_PCT)
      .satisfies(rules.required);

    form.validate(LEAD_TIME).satisfies(rules.requiredAllowZero);
    form.validate(TRACE_TYPE).satisfies(rules.required);
    form.validate(COST).satisfies(rules.isGreaterThanOrEqualTo(0));
    if (values.reserve_for_sale_quantity) {
      form
        .validate(QUANTITY)
        .satisfies(
          rules.isGreaterThanOrEqualTo(values.reserve_for_sale_quantity)
        );
    } else if (values.quantity_received) {
      form
        .validate(QUANTITY)
        .satisfies(rules.isGreaterThanOrEqualTo(values.quantity_received));
    } else {
      form.validate(QUANTITY).satisfies(rules.isGreaterThan(0));
    }

    form.validate(PERCENT_INSURED).satisfies(rules.isBetween(1, 100));
    if (!skipValidationForPurchase) {
      form.validate(SUGGESTED_RETAIL, SRP_CONDITION).satisfies(rules.required);
    }

    if (!values.purchase_for_repair_assist && !isConsignment) {
      form.validate(TOOL_USED).satisfies(rules.required);
    }

    if (values.hazmat && createdAt.isAfter(moment(validationDate))) {
      form
        .validate(HAZMAT_FEE)
        .satisfies(rules.required, rules.isGreaterThanOrEqualTo(0));
    }

    if (values.purchase_for_stock) {
      form.validate(SRP_CONDITION).satisfies(rules.matches(CONDITION));
    }

    if (
      (props && props.domLifeYears && props.llp) ||
      (props.selectedPart && props.selectedPart.dom_sensitive)
    ) {
      form.validate(DOM).satisfies(rules.required);
    }

    if (
      (values.purchase_for_repair ||
        (values.condition === AS_REMOVED &&
          !values.purchase_for_sale &&
          !values.purchase_for_stock)) &&
      createdAt.isAfter(preferredRepairStationRequiredValidationAfterDate) &&
      ((props.selectedLineItem && !props.selectedLineItem.consignment) ||
        !props.selectedLineItem)
    ) {
      form.validate(PREFERRED_REPAIR_STATION_ID).satisfies(rules.required);
    }

    if ([OVERHAULED, INSPECTED, REPAIRED].includes(values.condition)) {
      form.validate(TAG_DATE).satisfies(rules.required);
      form.validate(TAGGED_BY).satisfies(rules.required);
    }

    if (values.purchase_for_exchange) {
      form
        .validate(CORE_DUE_DAYS, CORE_RETURN_DATE, EXCHANGE_FEE)
        .satisfies(rules.required);
      form.validate(QUANTITY).satisfies(rules.mustBeOne);

      if (values.flat_exchange) {
        form.validate(CORE_REPAIR_FEE_NOT_TO_EXCEED).satisfies(rules.required);
      }
    }

    if (values.exchange_srp && props.minExchangeSrp && !isConsignment) {
      form
        .validate(EXCHANGE_SRP)
        .satisfies(rules.isGreaterThanOrEqualTo(props.minExchangeSrp));
    } else if (values.exchange_srp && !isConsignment) {
      form.validate(EXCHANGE_SRP).satisfies(rules.isGreaterThan(0));
    }

    if (values.do_not_exchange) {
      form.validate(INTERNAL_NOTE).satisfies(rules.required);
    }

    if (values[UNSELLABLE_PER_USER]) {
      form.validate(DO_NOT_SELL_REASON).satisfies(rules.required);
    }

    validatePurchaseForFields(values, form);
    validatePurchaseForSaleFields(values, form);
  });
}



// WEBPACK FOOTER //
// ./app/modules/PurchaseOrders/components/Detail/LineItemsTable/validate.js